import { observer } from "mobx-react";
import { useCallback } from "react";
import { StyleSheet } from "react-native";
import { useResponsive } from "~/hooks/useResponsive";
import type { SpeciesInstance } from "~/mobx/entities/species/Species";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { AnimalAvatar } from "./AnimalAvatar";
import { Pressable, type PressableState } from "./Pressable";
import { BodyText } from "./Text/BodyText";

const S = StyleSheet.create({
  card: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 9,
    backgroundColor: styleConstantsV2.colorPrimaryBg,
    borderRadius: styleConstantsV2.borderRadius8,
  },
});

export type Specie = "dog" | "cat" | "rabbit"; // TODO: move this type to a global type file

interface SpeciesCardSimpleProps {
  specie: SpeciesInstance;
  isSelected: boolean;
  onPress?: (specie: string) => void;
  isError?: boolean;
}

export const SpeciesCardSimple = observer(function SpeciesCardSimple({
  specie,
  isSelected,
  onPress,
  isError,
}: SpeciesCardSimpleProps) {
  const r = useResponsive();

  const resolveBackgroundColor = useCallback(
    (state: PressableState) => {
      if (isError) {
        return styleConstantsV2.colorErrorBg;
      }

      if (state.hovered || state.pressed || isSelected) {
        return styleConstantsV2.colorPrimaryHoverLight;
      }

      return styleConstantsV2.colorPrimaryBg;
    },
    [isError, isSelected],
  );

  return (
    <Pressable
      onPress={() => {
        onPress && onPress(specie.id);
      }}
      style={(state) => {
        return [
          S.card,
          {
            backgroundColor: resolveBackgroundColor(state),
            borderWidth: 2,
            borderColor: isError ? styleConstantsV2.colorError : "transparent",
            paddingHorizontal: r(20, 60, 60),
          },
        ];
      }}
    >
      <AnimalAvatar specie={specie} />
      <BodyText>{specie.name}</BodyText>
    </Pressable>
  );
});
